import { Button, Checkbox, Spinner, TextInput } from "flowbite-react"
import { useFormikContext } from "formik"
import React from "react"

import { CloseCircleIcon } from "@assets/icons"
import DangerouslyHTMLTextPriview from "@components/elements/DangerouslyHTMLTextPriview"
import FormGroup from "@components/elements/FormGroup"
import MonoSelect from "@components/elements/Select"
import ValidPromoCodeIcon from "@icons/ValidPromoCode"
import { PaymentAmountDetails } from "@modules/tenants/OrdersPage/OrderSummaryPage/PaymentModal/CashForm"
import { TipType } from "@mono/essentials/types/CartTypes"
import { IDomain } from "@mono/essentials/types/UserTypes"

import { TippingFormAttributes } from "."

export interface PreviewTipProps {
  isWidgetsCard: boolean
  domainDetails: IDomain
  tipData: {
    tip_number: string
    tip_options: {
      amount: number
    }[]
    tip_notes: string
    tip_type: string
    is_custom_tip_allowed?: boolean
  }
  handleTipping: (tip: number, tipType: string) => Promise<void>
  formattedTotalTip: string | undefined
  removeTipping: () => void
  paymentAmountDetails: PaymentAmountDetails
}

const calculateTipAmount = (paymentAmountDetails: PaymentAmountDetails, tip: number) => {
  return parseFloat(
    `${((paymentAmountDetails?.subTotalAmount ? paymentAmountDetails?.subTotalAmount : paymentAmountDetails?.totalAmount ? paymentAmountDetails?.totalAmount : paymentAmountDetails?.totalAmountWithTip) * tip) / 100}`
  ).toFixed(2)
}

const PreviewTip: React.FC<PreviewTipProps> = ({
  isWidgetsCard,
  domainDetails,
  tipData,
  handleTipping,
  formattedTotalTip,
  removeTipping,
  paymentAmountDetails,
}) => {
  const currency = domainDetails?.currency_symbol
  const {
    isSubmitting,
    setFieldValue,
    getFieldProps,
    errors,
    values,
    touched,
    setFieldTouched,
    setSubmitting,
    isValid,
  } = useFormikContext<TippingFormAttributes>()

  return (
    <div className={isWidgetsCard ? "w-full" : "sm:pl-28"}>
      <div
        className={`flex flex-col gap-2 border border-neutral-450 ${
          tipData?.tip_number === "single" ? "sm:p-4 p-2" : "lg:p-6 md:p-5 sm:p-4 p-2"
        } rounded-md w-full`}
      >
        <DangerouslyHTMLTextPriview
          content={tipData.tip_notes}
          className={"text14Medium text-gray-600"}
        />
        <div className={"flex gap-3"}>
          <MonoSelect
            {...getFieldProps("tip_status")}
            options={(() => {
              const tipOptions: any = (tipData.tip_options || [])
                .filter((option) => option?.amount > 0)
                .map((option: any) => ({
                  amount: parseFloat(option.amount).toFixed(2),
                  value: `fixed-${parseFloat(option.amount).toFixed(2)}`,
                  label: (
                    <div className={"flex justify-between"}>
                      <div>{`${tipData?.tip_type === "fixed" ? currency : ""}${option.amount}${
                        tipData?.tip_type === "percent" ? "%" : ""
                      } (Thank You!)`}</div>
                      {paymentAmountDetails ? (
                        <div>
                          {tipData?.tip_type === "percent"
                            ? `${currency}${calculateTipAmount(paymentAmountDetails, option?.amount)}`
                            : ""}
                        </div>
                      ) : null}
                    </div>
                  ),
                }))

              // If custom tipping is allowed, add custom options
              if (tipData.is_custom_tip_allowed) {
                if (tipData?.tip_type === "percent") {
                  tipOptions.push({
                    value: "custom",
                    label: `Custom Percentage (Thank You!)`,
                    amount: 0,
                  })
                } else if (tipData?.tip_type === "fixed") {
                  tipOptions.push({
                    value: "custom",
                    label: `Custom Amount (Thank You!)`,
                    amount: 0,
                  })
                }
              }

              return tipOptions
            })()}
            className={"z-15"}
            onValueChange={async (newValue: any) => {
              if (newValue.value !== "custom") {
                setFieldValue("tip", newValue?.amount)
                setFieldValue("tip_status", newValue?.value)
                setSubmitting(true)
                await handleTipping?.(newValue?.amount, tipData?.tip_type)
                setSubmitting(false)
              } else {
                setFieldValue("tip", 0)
                setFieldValue("tip_status", newValue?.value)
              }
            }}
            placeholder={"Add Tipping"}
          />

          {values?.tip_status === "custom" ? (
            <div className={"flex flex-row items-end gap-3"}>
              <FormGroup name={"tip"} requiredField className={"flex-1 min-w-[176px]"}>
                <div className={"flex items-center gap-2"}>
                  <div className={"relative block w-full"}>
                    <TextInput
                      {...getFieldProps("tip")}
                      onWheel={(e: React.WheelEvent<HTMLInputElement>) => {
                        const target = e.target as HTMLInputElement
                        if (target.value) {
                          target.blur()
                        }
                      }}
                      placeholder={tipData?.tip_type === "fixed" ? "0.0" : "0"}
                      className={"bg-whiteBlack-100"}
                      color={errors.tip && touched.tip ? "failure" : undefined}
                      min={0}
                    />
                  </div>
                  <Button
                    size={"xs"}
                    color={"primary"}
                    className={"!shadow-[0px 2px 5px rgba(42, 93, 227, 0.3)] mb-0.5"}
                    disabled={values.tip === undefined || isSubmitting || !isValid}
                    onClick={async () => {
                      setSubmitting(true)
                      await handleTipping(values.tip, tipData?.tip_type)
                      setSubmitting(false)
                      setFieldTouched("tip", false)
                    }}
                  >
                    {isSubmitting ? <Spinner size={"sm"} light /> : "Save"}
                  </Button>
                </div>
              </FormGroup>
            </div>
          ) : null}
        </div>
        {formattedTotalTip ? (
          <div className={"flex flex-row items-center justify-between gap-4"}>
            <div className={"flex flex-row items-center justify-start gap-2"}>
              <ValidPromoCodeIcon width={16} height={16} />
              <span className={"text-neutral-1000 text14Semibold"}>
                Tipping Added -{" "}
                <span className={"uppercase text-green-700"}>{formattedTotalTip}</span>
              </span>
              <div className={"flex flex-row items-center justify-end gap-1"}>
                <CloseCircleIcon
                  width={20}
                  height={20}
                  className={"cursor-pointer text-neutral-500"}
                  onClick={removeTipping}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default PreviewTip
